import { useState } from 'react';
import {
  Modal,
  PinInput,
  Button,
  Text,
  Stack,
  Group,
  Anchor,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconMail } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

interface ApiErrorLike {
  code?: string;
  message?: string;
  status?: number;
}

/**
 * Props for VerificationCodeModal component
 *
 * Story 1.4 Task 1: Create VerificationCodeModal component
 */
export interface VerificationCodeModalProps {
  /** Whether the modal is open */
  opened: boolean;
  /** Email address that received the verification code */
  email: string;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when user submits verification code */
  onVerify: (code: string) => Promise<void>;
  /** Callback when user clicks "Skip for now" */
  onSkip: () => void;
  /** Callback when user clicks "Resend code" */
  onResend: () => Promise<void>;
}

/**
 * VerificationCodeModal - Modal for email verification code entry
 *
 * Displays after registration to allow users to enter the 6-digit
 * verification code sent to their email address.
 *
 * @example
 * ```tsx
 * const [opened, { open, close }] = useDisclosure(false);
 *
 * <VerificationCodeModal
 *   opened={opened}
 *   email="user@example.com"
 *   onClose={close}
 *   onVerify={async (code) => { await verifyEmail(code); }}
 *   onSkip={() => { navigate('/'); }}
 *   onResend={async () => { await resendCode(); }}
 * />
 * ```
 */
export function VerificationCodeModal({
  opened,
  email,
  onClose,
  onVerify,
  onSkip,
  onResend,
}: VerificationCodeModalProps) {
  const { t } = useTranslation('common');
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const getErrorMessage = (err: unknown): string => {
    const apiError = err as ApiErrorLike;
    const errorCode = apiError?.code;
    const status = apiError?.status;

    // Handle rate limiting (429)
    if (status === 429 || errorCode === 'RATE_LIMIT_ERROR') {
      return t('auth_pages.verification.too_many_attempts');
    }

    // Handle validation errors (400)
    if (status === 400 || errorCode === 'VALIDATION_ERROR') {
      const message = apiError?.message?.toLowerCase() || '';
      // Check for specific error messages from backend
      if (message.includes('expired')) {
        return t('auth_pages.verification.code_expired');
      }
      if (message.includes('invalid') || message.includes('remaining')) {
        return t('auth_pages.verification.invalid_code');
      }
    }

    // Fallback to error message or generic error
    if (apiError?.message) {
      return apiError.message;
    }

    return t('auth_pages.verification.verify_error');
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError(t('auth_pages.verification.code_incomplete'));
      return;
    }

    setError(null);
    setIsVerifying(true);
    try {
      await onVerify(code);
      // Show success notification
      notifications.show({
        title: t('auth_pages.verification.title'),
        message: t('auth_pages.verification.verify_success'),
        color: 'green',
      });
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      // Clear input on error for retry
      setCode('');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setIsResending(true);
    setError(null);
    try {
      await onResend();
      // Start 60s cooldown
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth_pages.verification.resend_error'));
    } finally {
      setIsResending(false);
    }
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
    setError(null);
  };

  const handleCodeComplete = (value: string) => {
    setCode(value);
    // Auto-submit when code is complete
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconMail size={20} />
          <Text fw={600}>{t('auth_pages.verification.title')}</Text>
        </Group>
      }
      centered
      size="md"
      closeOnClickOutside={false}
      closeOnEscape={!isVerifying}
      withCloseButton={!isVerifying}
      data-testid="verification-code-modal"
    >
      <Stack gap="lg">
        {/* Email info */}
        <Text size="sm" ta="center">
          {t('auth_pages.verification.sent_to')}
        </Text>
        <Text size="sm" fw={600} ta="center" data-testid="verification-email">
          {email}
        </Text>

        {/* Code input */}
        <Stack gap="xs" align="center">
          <Text size="sm" c="dimmed">
            {t('auth_pages.verification.enter_code')}
          </Text>
          <PinInput
            length={6}
            type="number"
            size="xl"
            value={code}
            onChange={handleCodeChange}
            onComplete={handleCodeComplete}
            autoFocus
            oneTimeCode
            placeholder=""
            aria-label={t('auth_pages.verification.code_label')}
            data-testid="pin-input"
            error={!!error}
            disabled={isVerifying}
            styles={{
              input: {
                fontFamily: 'monospace',
                fontSize: '1.5rem',
                textAlign: 'center',
              },
            }}
          />
          {error && (
            <Text size="xs" c="red" ta="center" data-testid="error-message">
              {error}
            </Text>
          )}
        </Stack>

        {/* Verify button */}
        <Button
          fullWidth
          onClick={handleVerify}
          loading={isVerifying}
          disabled={code.length !== 6}
          data-testid="verify-button"
        >
          {t('auth_pages.verification.verify_button')}
        </Button>

        {/* Resend link */}
        <Group justify="center" gap="xs">
          <Text size="sm" c="dimmed">
            {t('auth_pages.verification.didnt_receive')}
          </Text>
          <Anchor
            component="button"
            type="button"
            size="sm"
            onClick={handleResend}
            disabled={isResending || resendCooldown > 0}
            data-testid="resend-button"
          >
            {resendCooldown > 0
              ? t('auth_pages.verification.resend_cooldown', { seconds: resendCooldown })
              : t('auth_pages.verification.resend_code')}
          </Anchor>
        </Group>

        {/* Skip link */}
        <Anchor
          component="button"
          type="button"
          size="sm"
          c="dimmed"
          ta="center"
          onClick={onSkip}
          disabled={isVerifying}
          style={{ display: 'block' }}
          data-testid="skip-button"
        >
          {t('auth_pages.verification.skip_for_now')}
        </Anchor>
      </Stack>
    </Modal>
  );
}
